/**
 * Mock data for job search functionality
 * Comprehensive dataset with 6 companies across different industries:
 * - Each company has exactly 10 job positions and 5 locations (except tech-innovations-global with 2 locations)
 * - Distribution: 1 loc (1 job), 1 loc (10 jobs), 1 loc (6 jobs), 1 loc (3 jobs), 1 loc (2 jobs) per company
 * - At least 5 jobs are closed across all locations for testing
 * - All data fields populated for proper routing functionality
 */

const now = new Date();

// 6 main companies across different industries - FIXED WITH CONSISTENT SLUGS + ADDED TECH-INNOVATIONS-GLOBAL
const companies = [
  {
    id: 'company-1',
    slug: 'techglobal-solutions',
    name: 'TechGlobal Solutions',
    logoUrl: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=48&h=48&fit=crop&crop=center',
    industry: 'Software Development',
    sizeRange: '501-1000 employees',
    hqCity: 'San Francisco',
    hqRegion: 'California',
    summary: 'Global software development company with offices in major tech hubs, specializing in enterprise applications and cloud solutions.',
    openRoles: 10,
    locations: ['San Francisco, CA', 'New York, NY', 'Austin, TX', 'Seattle, WA', 'Chicago, IL']
  },
  {
    id: 'company-2',
    slug: 'globalealth-innovations',
    name: 'GlobalHealth Innovations',
    logoUrl: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=48&h=48&fit=crop&crop=center',
    industry: 'Healthcare Technology',
    sizeRange: '1000+ employees',
    hqCity: 'Boston',
    hqRegion: 'Massachusetts',
    summary: 'Leading healthcare technology company revolutionizing patient care through innovative medical devices and digital health solutions.',
    openRoles: 10,
    locations: ['Boston, MA', 'Atlanta, GA', 'Phoenix, AZ', 'Denver, CO', 'Minneapolis, MN']
  },
  {
    id: 'company-3',
    slug: 'sustainable-energy-corp',
    name: 'Sustainable Energy Corp',
    logoUrl: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=48&h=48&fit=crop&crop=center',
    industry: 'Renewable Energy',
    sizeRange: '201-500 employees',
    hqCity: 'Portland',
    hqRegion: 'Oregon',
    summary: 'Pioneering renewable energy solutions with focus on solar, wind, and energy storage systems across North America.',
    openRoles: 10,
    locations: ['Portland, OR', 'Sacramento, CA', 'Austin, TX', 'Boulder, CO', 'Raleigh, NC']
  },
  {
    id: 'company-4',
    slug: 'fintech-dynamics',
    name: 'FinTech Dynamics',
    logoUrl: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=48&h=48&fit=crop&crop=center',
    industry: 'Financial Technology',
    sizeRange: '101-500 employees',
    hqCity: 'New York',
    hqRegion: 'New York',
    summary: 'Revolutionary fintech company developing next-generation payment solutions, blockchain technology, and digital banking platforms.',
    openRoles: 10,
    locations: ['New York, NY', 'Miami, FL', 'Dallas, TX', 'Charlotte, NC', 'Nashville, TN']
  },
  {
    id: 'company-5',
    slug: 'edutech-innovations',
    name: 'EduTech Innovations',
    logoUrl: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=48&h=48&fit=crop&crop=center',
    industry: 'Education Technology',
    sizeRange: '51-200 employees',
    hqCity: 'San Jose',
    hqRegion: 'California',
    summary: 'Transforming education through innovative learning platforms, AI-powered tutoring systems, and immersive educational experiences.',
    openRoles: 10,
    locations: ['San Jose, CA', 'Ann Arbor, MI', 'Cambridge, MA', 'Research Triangle Park, NC', 'Salt Lake City, UT']
  },
  // NEW: Added Tech Innovations Global to match mockJobsData.js
  {
    id: 'company-6',
    slug: 'tech-innovations-global',
    name: 'Tech Innovations Global',
    logoUrl: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=48&h=48&fit=crop&crop=center',
    industry: 'Artificial Intelligence',
    sizeRange: '201-500 employees',
    hqCity: 'San Francisco',
    hqRegion: 'California',
    summary: 'Pioneering technology company at the forefront of AI-driven solutions and cloud infrastructure that transform how businesses operate.',
    openRoles: 2,
    locations: ['San Francisco, CA', 'Remote']
  }
];

// COMPLETELY REBUILT ALL 27 LOCATIONS WITH CONSISTENT PATTERNS + ADDED TECH-INNOVATIONS-GLOBAL LOCATIONS
const locations = [
  // TechGlobal Solutions locations (company-1) - 10,6,3,2,1 job distribution
  {
    id: 'loc-tech-sf',
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    addressLine1: '123 Market Street',
    addressLine2: 'Suite 4000',
    city: 'San Francisco',
    region: 'California',
    postalCode: '94105',
    country: 'USA',
    totalRoles: 10,
    activeRoles: 9,
    jobPreviews: [
      { id: 'tech-sf-1', title: 'Senior Software Engineer', status: 'ACTIVE' },
      { id: 'tech-sf-2', title: 'DevOps Engineer', status: 'ACTIVE' },
      { id: 'tech-sf-3', title: 'Product Manager', status: 'ACTIVE' },
      { id: 'tech-sf-4', title: 'Data Scientist', status: 'ACTIVE' },
      { id: 'tech-sf-5', title: 'Cloud Architect', status: 'ACTIVE' },
      { id: 'tech-sf-6', title: 'Security Engineer', status: 'CLOSED' },
      { id: 'tech-sf-7', title: 'Mobile Developer', status: 'ACTIVE' },
      { id: 'tech-sf-8', title: 'UI/UX Designer', status: 'ACTIVE' },
      { id: 'tech-sf-9', title: 'Technical Lead', status: 'ACTIVE' },
      { id: 'tech-sf-10', title: 'Site Reliability Engineer', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-tech-ny',
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    addressLine1: '456 Broadway',
    addressLine2: 'Floor 25',
    city: 'New York',
    region: 'New York',
    postalCode: '10013',
    country: 'USA',
    totalRoles: 6,
    activeRoles: 5,
    jobPreviews: [
      { id: 'tech-ny-1', title: 'Frontend Developer', status: 'ACTIVE' },
      { id: 'tech-ny-2', title: 'Backend Developer', status: 'ACTIVE' },
      { id: 'tech-ny-3', title: 'Full Stack Developer', status: 'ACTIVE' },
      { id: 'tech-ny-4', title: 'QA Engineer', status: 'CLOSED' },
      { id: 'tech-ny-5', title: 'Business Analyst', status: 'ACTIVE' },
      { id: 'tech-ny-6', title: 'Scrum Master', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-tech-austin',
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    addressLine1: '789 Congress Ave',
    addressLine2: 'Building C',
    city: 'Austin',
    region: 'Texas',
    postalCode: '78701',
    country: 'USA',
    totalRoles: 3,
    activeRoles: 3,
    jobPreviews: [
      { id: 'tech-austin-1', title: 'Database Administrator', status: 'ACTIVE' },
      { id: 'tech-austin-2', title: 'Systems Administrator', status: 'ACTIVE' },
      { id: 'tech-austin-3', title: 'Network Engineer', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-tech-seattle',
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    addressLine1: '321 Pine Street',
    addressLine2: 'Suite 1200',
    city: 'Seattle',
    region: 'Washington',
    postalCode: '98101',
    country: 'USA',
    totalRoles: 2,
    activeRoles: 2,
    jobPreviews: [
      { id: 'tech-seattle-1', title: 'Machine Learning Engineer', status: 'ACTIVE' },
      { id: 'tech-seattle-2', title: 'Research Scientist', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-tech-chicago',
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    addressLine1: '654 Lake Shore Dr',
    addressLine2: 'Tower 2',
    city: 'Chicago',
    region: 'Illinois',
    postalCode: '60611',
    country: 'USA',
    totalRoles: 1,
    activeRoles: 1,
    jobPreviews: [
      { id: 'tech-chicago-1', title: 'Technical Writer', status: 'ACTIVE' }
    ]
  },

  // GlobalHealth Innovations locations (company-2) - 10,6,3,2,1 job distribution
  {
    id: 'loc-health-boston',
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    addressLine1: '100 Cambridge Street',
    addressLine2: 'Medical District',
    city: 'Boston',
    region: 'Massachusetts',
    postalCode: '02114',
    country: 'USA',
    totalRoles: 10,
    activeRoles: 9,
    jobPreviews: [
      { id: 'health-boston-1', title: 'Medical Device Engineer', status: 'ACTIVE' },
      { id: 'health-boston-2', title: 'Clinical Data Scientist', status: 'ACTIVE' },
      { id: 'health-boston-3', title: 'Regulatory Affairs Specialist', status: 'ACTIVE' },
      { id: 'health-boston-4', title: 'Biomedical Engineer', status: 'ACTIVE' },
      { id: 'health-boston-5', title: 'Research Scientist', status: 'ACTIVE' },
      { id: 'health-boston-6', title: 'Clinical Trial Manager', status: 'ACTIVE' },
      { id: 'health-boston-7', title: 'Medical Software Developer', status: 'CLOSED' },
      { id: 'health-boston-8', title: 'Quality Assurance Specialist', status: 'ACTIVE' },
      { id: 'health-boston-9', title: 'Healthcare Data Analyst', status: 'ACTIVE' },
      { id: 'health-boston-10', title: 'Medical AI Researcher', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-health-atlanta',
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    addressLine1: '250 Williams Street NW',
    addressLine2: 'Healthcare Plaza',
    city: 'Atlanta',
    region: 'Georgia',
    postalCode: '30303',
    country: 'USA',
    totalRoles: 6,
    activeRoles: 5,
    jobPreviews: [
      { id: 'health-atlanta-1', title: 'Biomedical Researcher', status: 'ACTIVE' },
      { id: 'health-atlanta-2', title: 'Clinical Research Coordinator', status: 'ACTIVE' },
      { id: 'health-atlanta-3', title: 'Medical Device Tester', status: 'ACTIVE' },
      { id: 'health-atlanta-4', title: 'Pharmaceutical Analyst', status: 'CLOSED' },
      { id: 'health-atlanta-5', title: 'Healthcare Systems Analyst', status: 'ACTIVE' },
      { id: 'health-atlanta-6', title: 'Medical Technical Writer', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-health-phoenix',
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    addressLine1: '440 N 5th Street',
    addressLine2: 'Innovation Center',
    city: 'Phoenix',
    region: 'Arizona',
    postalCode: '85004',
    country: 'USA',
    totalRoles: 3,
    activeRoles: 3,
    jobPreviews: [
      { id: 'health-phoenix-1', title: 'Telemedicine Specialist', status: 'ACTIVE' },
      { id: 'health-phoenix-2', title: 'Health Informatics Analyst', status: 'ACTIVE' },
      { id: 'health-phoenix-3', title: 'Medical Product Manager', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-health-denver',
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    addressLine1: '1515 Wynkoop Street',
    addressLine2: 'Biotech Building',
    city: 'Denver',
    region: 'Colorado',
    postalCode: '80202',
    country: 'USA',
    totalRoles: 2,
    activeRoles: 2,
    jobPreviews: [
      { id: 'health-denver-1', title: 'Medical Compliance Officer', status: 'ACTIVE' },
      { id: 'health-denver-2', title: 'Clinical Operations Manager', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-health-minneapolis',
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    addressLine1: '800 Nicollet Mall',
    addressLine2: 'Medical Tower',
    city: 'Minneapolis',
    region: 'Minnesota',
    postalCode: '55402',
    country: 'USA',
    totalRoles: 1,
    activeRoles: 1,
    jobPreviews: [
      { id: 'health-minneapolis-1', title: 'Senior Medical Consultant', status: 'ACTIVE' }
    ]
  },

  // NEW: Tech Innovations Global locations (company-6) - 2 locations: 1 physical + 1 remote
  {
    id: 'loc-job-senior-fullstack-sf-0',
    companyId: 'company-6',
    companySlug: 'tech-innovations-global',
    companyName: 'Tech Innovations Global',
    addressLine1: '1455 Market Street',
    addressLine2: 'Suite 1600',
    city: 'San Francisco',
    region: 'California',
    postalCode: '94103',
    country: 'USA',
    totalRoles: 1,
    activeRoles: 1,
    jobPreviews: [
      { id: 'job-senior-fullstack-sf', title: 'Senior Full-Stack Engineer', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-product-manager-remote-0',
    companyId: 'company-6',
    companySlug: 'tech-innovations-global',
    companyName: 'Tech Innovations Global',
    addressLine1: null,
    addressLine2: null,
    city: 'Remote',
    region: 'Global',
    postalCode: null,
    country: 'Global',
    totalRoles: 1,
    activeRoles: 1,
    jobPreviews: [
      { id: 'job-product-manager-remote', title: 'Senior Product Manager - AI Platform', status: 'ACTIVE' }
    ]
  },

  // Sustainable Energy Corp locations (company-3) - 10,6,3,2,1 job distribution - COMPLETELY REBUILT
  {
    id: 'loc-energy-portland',
    companyId: 'company-3',
    companySlug: 'sustainable-energy-corp',
    companyName: 'Sustainable Energy Corp',
    addressLine1: '900 SW 5th Avenue',
    addressLine2: 'Green Building',
    city: 'Portland',
    region: 'Oregon',
    postalCode: '97204',
    country: 'USA',
    totalRoles: 10,
    activeRoles: 9,
    jobPreviews: [
      { id: 'energy-portland-1', title: 'Solar Engineer', status: 'ACTIVE' },
      { id: 'energy-portland-2', title: 'Wind Technician', status: 'ACTIVE' },
      { id: 'energy-portland-3', title: 'Environmental Consultant', status: 'ACTIVE' },
      { id: 'energy-portland-4', title: 'Energy Storage Specialist', status: 'ACTIVE' },
      { id: 'energy-portland-5', title: 'Grid Integration Engineer', status: 'ACTIVE' },
      { id: 'energy-portland-6', title: 'Sustainability Manager', status: 'ACTIVE' },
      { id: 'energy-portland-7', title: 'Clean Energy Researcher', status: 'ACTIVE' },
      { id: 'energy-portland-8', title: 'Battery Technology Engineer', status: 'CLOSED' },
      { id: 'energy-portland-9', title: 'Project Manager', status: 'ACTIVE' },
      { id: 'energy-portland-10', title: 'Energy Policy Analyst', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-energy-sacramento',
    companyId: 'company-3',
    companySlug: 'sustainable-energy-corp',
    companyName: 'Sustainable Energy Corp',
    addressLine1: '1330 21st Street',
    addressLine2: 'Renewable Center',
    city: 'Sacramento',
    region: 'California',
    postalCode: '95811',
    country: 'USA',
    totalRoles: 6,
    activeRoles: 6,
    jobPreviews: [
      { id: 'energy-sacramento-1', title: 'Renewable Energy Analyst', status: 'ACTIVE' },
      { id: 'energy-sacramento-2', title: 'Field Service Engineer', status: 'ACTIVE' },
      { id: 'energy-sacramento-3', title: 'Solar Installation Manager', status: 'ACTIVE' },
      { id: 'energy-sacramento-4', title: 'Energy Efficiency Consultant', status: 'ACTIVE' },
      { id: 'energy-sacramento-5', title: 'Maintenance Supervisor', status: 'ACTIVE' },
      { id: 'energy-sacramento-6', title: 'Power Systems Engineer', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-energy-austin',
    companyId: 'company-3',
    companySlug: 'sustainable-energy-corp',
    companyName: 'Sustainable Energy Corp',
    addressLine1: '301 Congress Avenue',
    addressLine2: 'Energy Plaza',
    city: 'Austin',
    region: 'Texas',
    postalCode: '78701',
    country: 'USA',
    totalRoles: 3,
    activeRoles: 2,
    jobPreviews: [
      { id: 'energy-austin-1', title: 'Energy Market Analyst', status: 'ACTIVE' },
      { id: 'energy-austin-2', title: 'Business Development Manager', status: 'CLOSED' },
      { id: 'energy-austin-3', title: 'Regulatory Affairs Specialist', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-energy-boulder',
    companyId: 'company-3',
    companySlug: 'sustainable-energy-corp',
    companyName: 'Sustainable Energy Corp',
    addressLine1: '2445 Pearl Street',
    addressLine2: 'Innovation Hub',
    city: 'Boulder',
    region: 'Colorado',
    postalCode: '80302',
    country: 'USA',
    totalRoles: 2,
    activeRoles: 2,
    jobPreviews: [
      { id: 'energy-boulder-1', title: 'R&D Engineer', status: 'ACTIVE' },
      { id: 'energy-boulder-2', title: 'Sustainability Coordinator', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-energy-raleigh',
    companyId: 'company-3',
    companySlug: 'sustainable-energy-corp',
    companyName: 'Sustainable Energy Corp',
    addressLine1: '4601 Six Forks Road',
    addressLine2: 'Tech Park',
    city: 'Raleigh',
    region: 'North Carolina',
    postalCode: '27609',
    country: 'USA',
    totalRoles: 1,
    activeRoles: 1,
    jobPreviews: [
      { id: 'energy-raleigh-1', title: 'Operations Director', status: 'ACTIVE' }
    ]
  },

  // FinTech Dynamics locations (company-4) - 10,6,3,2,1 job distribution - COMPLETELY REBUILT
  {
    id: 'loc-fintech-ny',
    companyId: 'company-4',
    companySlug: 'fintech-dynamics',
    companyName: 'FinTech Dynamics',
    addressLine1: '200 West Street',
    addressLine2: 'Financial District',
    city: 'New York',
    region: 'New York',
    postalCode: '10282',
    country: 'USA',
    totalRoles: 10,
    activeRoles: 8,
    jobPreviews: [
      { id: 'fintech-ny-1', title: 'Blockchain Developer', status: 'ACTIVE' },
      { id: 'fintech-ny-2', title: 'Financial Analyst', status: 'ACTIVE' },
      { id: 'fintech-ny-3', title: 'Payment Systems Engineer', status: 'ACTIVE' },
      { id: 'fintech-ny-4', title: 'Risk Manager', status: 'ACTIVE' },
      { id: 'fintech-ny-5', title: 'Compliance Officer', status: 'CLOSED' },
      { id: 'fintech-ny-6', title: 'Cybersecurity Specialist', status: 'ACTIVE' },
      { id: 'fintech-ny-7', title: 'Product Manager', status: 'ACTIVE' },
      { id: 'fintech-ny-8', title: 'Quantitative Analyst', status: 'CLOSED' },
      { id: 'fintech-ny-9', title: 'Digital Banking Specialist', status: 'ACTIVE' },
      { id: 'fintech-ny-10', title: 'API Developer', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-fintech-miami',
    companyId: 'company-4',
    companySlug: 'fintech-dynamics',
    companyName: 'FinTech Dynamics',
    addressLine1: '1450 Brickell Avenue',
    addressLine2: 'Suite 2200',
    city: 'Miami',
    region: 'Florida',
    postalCode: '33131',
    country: 'USA',
    totalRoles: 6,
    activeRoles: 6,
    jobPreviews: [
      { id: 'fintech-miami-1', title: 'Cryptocurrency Analyst', status: 'ACTIVE' },
      { id: 'fintech-miami-2', title: 'Trading Systems Developer', status: 'ACTIVE' },
      { id: 'fintech-miami-3', title: 'Financial Data Scientist', status: 'ACTIVE' },
      { id: 'fintech-miami-4', title: 'Investment Strategist', status: 'ACTIVE' },
      { id: 'fintech-miami-5', title: 'Portfolio Manager', status: 'ACTIVE' },
      { id: 'fintech-miami-6', title: 'Credit Risk Analyst', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-fintech-dallas',
    companyId: 'company-4',
    companySlug: 'fintech-dynamics',
    companyName: 'FinTech Dynamics',
    addressLine1: '1717 Main Street',
    addressLine2: 'Suite 5000',
    city: 'Dallas',
    region: 'Texas',
    postalCode: '75201',
    country: 'USA',
    totalRoles: 3,
    activeRoles: 3,
    jobPreviews: [
      { id: 'fintech-dallas-1', title: 'Mobile App Developer', status: 'ACTIVE' },
      { id: 'fintech-dallas-2', title: 'UX/UI Designer', status: 'ACTIVE' },
      { id: 'fintech-dallas-3', title: 'Customer Success Manager', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-fintech-charlotte',
    companyId: 'company-4',
    companySlug: 'fintech-dynamics',
    companyName: 'FinTech Dynamics',
    addressLine1: '100 N Tryon Street',
    addressLine2: 'Banking Center',
    city: 'Charlotte',
    region: 'North Carolina',
    postalCode: '28255',
    country: 'USA',
    totalRoles: 2,
    activeRoles: 2,
    jobPreviews: [
      { id: 'fintech-charlotte-1', title: 'Regulatory Technology Analyst', status: 'ACTIVE' },
      { id: 'fintech-charlotte-2', title: 'Business Intelligence Analyst', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-fintech-nashville',
    companyId: 'company-4',
    companySlug: 'fintech-dynamics',
    companyName: 'FinTech Dynamics',
    addressLine1: '222 2nd Avenue North',
    addressLine2: 'Music City Center',
    city: 'Nashville',
    region: 'Tennessee',
    postalCode: '37201',
    country: 'USA',
    totalRoles: 1,
    activeRoles: 0,
    jobPreviews: [
      { id: 'fintech-nashville-1', title: 'Operations Manager', status: 'CLOSED' }
    ]
  },

  // EduTech Innovations locations (company-5) - 10,6,3,2,1 job distribution - COMPLETELY REBUILT
  {
    id: 'loc-edutech-sj',
    companyId: 'company-5',
    companySlug: 'edutech-innovations',
    companyName: 'EduTech Innovations',
    addressLine1: '1 North First Street',
    addressLine2: 'Tech Campus',
    city: 'San Jose',
    region: 'California',
    postalCode: '95113',
    country: 'USA',
    totalRoles: 10,
    activeRoles: 10,
    jobPreviews: [
      { id: 'edutech-sj-1', title: 'Educational Software Engineer', status: 'ACTIVE' },
      { id: 'edutech-sj-2', title: 'Learning Analytics Specialist', status: 'ACTIVE' },
      { id: 'edutech-sj-3', title: 'Curriculum Designer', status: 'ACTIVE' },
      { id: 'edutech-sj-4', title: 'AI Tutor Developer', status: 'ACTIVE' },
      { id: 'edutech-sj-5', title: 'Educational Content Writer', status: 'ACTIVE' },
      { id: 'edutech-sj-6', title: 'Learning Experience Designer', status: 'ACTIVE' },
      { id: 'edutech-sj-7', title: 'Educational Data Scientist', status: 'ACTIVE' },
      { id: 'edutech-sj-8', title: 'Student Success Manager', status: 'ACTIVE' },
      { id: 'edutech-sj-9', title: 'EdTech Product Manager', status: 'ACTIVE' },
      { id: 'edutech-sj-10', title: 'Virtual Reality Developer', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-edutech-ann-arbor',
    companyId: 'company-5',
    companySlug: 'edutech-innovations',
    companyName: 'EduTech Innovations',
    addressLine1: '2200 Bonisteel Boulevard',
    addressLine2: 'University Research Park',
    city: 'Ann Arbor',
    region: 'Michigan',
    postalCode: '48109',
    country: 'USA',
    totalRoles: 6,
    activeRoles: 6,
    jobPreviews: [
      { id: 'edutech-aa-1', title: 'Educational Researcher', status: 'ACTIVE' },
      { id: 'edutech-aa-2', title: 'Instructional Designer', status: 'ACTIVE' },
      { id: 'edutech-aa-3', title: 'Assessment Specialist', status: 'ACTIVE' },
      { id: 'edutech-aa-4', title: 'Learning Platform Developer', status: 'ACTIVE' },
      { id: 'edutech-aa-5', title: 'Educational Psychologist', status: 'ACTIVE' },
      { id: 'edutech-aa-6', title: 'Academic Partnerships Manager', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-edutech-cambridge',
    companyId: 'company-5',
    companySlug: 'edutech-innovations',
    companyName: 'EduTech Innovations',
    addressLine1: '1 Broadway',
    addressLine2: 'Cambridge Innovation Center',
    city: 'Cambridge',
    region: 'Massachusetts',
    postalCode: '02142',
    country: 'USA',
    totalRoles: 3,
    activeRoles: 3,
    jobPreviews: [
      { id: 'edutech-cambridge-1', title: 'Machine Learning Engineer', status: 'ACTIVE' },
      { id: 'edutech-cambridge-2', title: 'Natural Language Processing Specialist', status: 'ACTIVE' },
      { id: 'edutech-cambridge-3', title: 'Educational Technology Consultant', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-edutech-rtp',
    companyId: 'company-5',
    companySlug: 'edutech-innovations',
    companyName: 'EduTech Innovations',
    addressLine1: '79 TW Alexander Drive',
    addressLine2: 'Research Triangle Park',
    city: 'Research Triangle Park',
    region: 'North Carolina',
    postalCode: '27709',
    country: 'USA',
    totalRoles: 2,
    activeRoles: 2,
    jobPreviews: [
      { id: 'edutech-rtp-1', title: 'Quality Assurance Engineer', status: 'ACTIVE' },
      { id: 'edutech-rtp-2', title: 'Technical Support Specialist', status: 'ACTIVE' }
    ]
  },
  {
    id: 'loc-edutech-slc',
    companyId: 'company-5',
    companySlug: 'edutech-innovations',
    companyName: 'EduTech Innovations',
    addressLine1: '36 S State Street',
    addressLine2: 'Suite 2000',
    city: 'Salt Lake City',
    region: 'Utah',
    postalCode: '84111',
    country: 'USA',
    totalRoles: 1,
    activeRoles: 1,
    jobPreviews: [
      { id: 'edutech-slc-1', title: 'Regional Sales Manager', status: 'ACTIVE' }
    ]
  }
];

// COMPLETELY REBUILT ALL 52 JOBS WITH CONSISTENT PATTERNS AND PROPER DISTRIBUTION + ADDED TECH-INNOVATIONS-GLOBAL JOBS
const jobs = [
  // TechGlobal Solutions jobs (22 total across 5 locations: 10+6+3+2+1)
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `tech-sf-${i + 1}`,
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    title: [
      'Senior Software Engineer', 'DevOps Engineer', 'Product Manager', 'Data Scientist', 'Cloud Architect',
      'Security Engineer', 'Mobile Developer', 'UI/UX Designer', 'Technical Lead', 'Site Reliability Engineer'
    ]?.[i],
    isFeatured: i === 0,
    descriptionHtml: `<p>Join our ${[
      'Senior Software Engineering', 'DevOps Engineering', 'Product Management', 'Data Science', 'Cloud Architecture',
      'Security Engineering', 'Mobile Development', 'UI/UX Design', 'Technical Leadership', 'Site Reliability Engineering'
    ]?.[i]} team at TechGlobal Solutions.</p>`,
    locations: [{ locationId: 'loc-tech-sf', city: 'San Francisco', region: 'California', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: 'HYBRID',
    salary: { currency: '$', min: 110000 + (i * 5000), max: 150000 + (i * 5000), unit: 'YEAR' },
    postedAt: new Date(now.getTime() - i * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + (45 - i * 2) * 24 * 60 * 60 * 1000)?.toISOString(),
    status: i === 5 ? 'CLOSED' : 'ACTIVE',
    locationStatuses: { 'loc-tech-sf': { status: i === 5 ? 'CLOSED' : 'ACTIVE', applications: 15 + i, validThrough: new Date(now.getTime() + (45 - i * 2) * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: ['React', 'Node.js', 'TypeScript', 'AWS']?.[Math.floor(i / 3)] ? [['React', 'Node.js'], ['TypeScript', 'AWS'], ['Docker', 'Kubernetes'], ['Python', 'ML']]?.[Math.floor(i / 3)] : ['React', 'Node.js'],
    experienceLevel: i < 3 ? 'SENIOR' : i < 7 ? 'MID' : 'JUNIOR',
    category: 'Engineering'
  })),

  ...Array.from({ length: 6 }, (_, i) => ({
    id: `tech-ny-${i + 1}`,
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    title: ['Frontend Developer', 'Backend Developer', 'Full Stack Developer', 'QA Engineer', 'Business Analyst', 'Scrum Master']?.[i],
    descriptionHtml: `<p>Join our ${['Frontend Development', 'Backend Development', 'Full Stack Development', 'Quality Assurance', 'Business Analysis', 'Scrum Master']?.[i]} team in New York.</p>`,
    locations: [{ locationId: 'loc-tech-ny', city: 'New York', region: 'New York', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: 'HYBRID',
    salary: { currency: '$', min: 90000 + (i * 5000), max: 130000 + (i * 5000), unit: 'YEAR' },
    postedAt: new Date(now.getTime() - (i + 10) * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + (40 - i * 2) * 24 * 60 * 60 * 1000)?.toISOString(),
    status: i === 3 ? 'CLOSED' : 'ACTIVE',
    locationStatuses: { 'loc-tech-ny': { status: i === 3 ? 'CLOSED' : 'ACTIVE', applications: 10 + i, validThrough: new Date(now.getTime() + (40 - i * 2) * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: [['React', 'JavaScript'], ['Node.js', 'Express'], ['Full Stack', 'MEAN'], ['Testing', 'Selenium'], ['Business Analysis', 'Agile'], ['Scrum', 'Agile']]?.[i],
    experienceLevel: i < 2 ? 'SENIOR' : i < 5 ? 'MID' : 'JUNIOR',
    category: i < 4 ? 'Engineering' : 'Product'
  })),

  ...Array.from({ length: 3 }, (_, i) => ({
    id: `tech-austin-${i + 1}`,
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    title: ['Database Administrator', 'Systems Administrator', 'Network Engineer']?.[i],
    descriptionHtml: `<p>Join our ${['Database Administration', 'Systems Administration', 'Network Engineering']?.[i]} team in Austin.</p>`,
    locations: [{ locationId: 'loc-tech-austin', city: 'Austin', region: 'Texas', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: 'ONSITE',
    salary: { currency: '$', min: 80000 + (i * 5000), max: 110000 + (i * 5000), unit: 'YEAR' },
    postedAt: new Date(now.getTime() - (i + 16) * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + (35 - i * 3) * 24 * 60 * 60 * 1000)?.toISOString(),
    status: 'ACTIVE',
    locationStatuses: { 'loc-tech-austin': { status: 'ACTIVE', applications: 8 + i, validThrough: new Date(now.getTime() + (35 - i * 3) * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: [['PostgreSQL', 'MySQL'], ['Linux', 'Docker'], ['Networking', 'Cisco']]?.[i],
    experienceLevel: 'MID',
    category: 'Engineering'
  })),

  ...Array.from({ length: 2 }, (_, i) => ({
    id: `tech-seattle-${i + 1}`,
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    title: ['Machine Learning Engineer', 'Research Scientist']?.[i],
    descriptionHtml: `<p>Join our ${['Machine Learning Engineering', 'Research Science']?.[i]} team in Seattle.</p>`,
    locations: [{ locationId: 'loc-tech-seattle', city: 'Seattle', region: 'Washington', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: 'HYBRID',
    salary: { currency: '$', min: 130000 + (i * 10000), max: 170000 + (i * 10000), unit: 'YEAR' },
    postedAt: new Date(now.getTime() - (i + 19) * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + (50 - i * 5) * 24 * 60 * 60 * 1000)?.toISOString(),
    status: 'ACTIVE',
    locationStatuses: { 'loc-tech-seattle': { status: 'ACTIVE', applications: 12 + i, validThrough: new Date(now.getTime() + (50 - i * 5) * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: [['Machine Learning', 'Python'], ['Research', 'AI']]?.[i],
    experienceLevel: 'SENIOR',
    category: 'Engineering'
  })),

  {
    id: 'tech-chicago-1',
    companyId: 'company-1',
    companySlug: 'techglobal-solutions',
    companyName: 'TechGlobal Solutions',
    title: 'Technical Writer',
    descriptionHtml: '<p>Join our Technical Writing team in Chicago.</p>',
    locations: [{ locationId: 'loc-tech-chicago', city: 'Chicago', region: 'Illinois', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: 'REMOTE',
    salary: { currency: '$', min: 70000, max: 90000, unit: 'YEAR' },
    postedAt: new Date(now.getTime() - 21 * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)?.toISOString(),
    status: 'ACTIVE',
    locationStatuses: { 'loc-tech-chicago': { status: 'ACTIVE', applications: 15, validThrough: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: ['Technical Writing', 'Documentation'],
    experienceLevel: 'MID',
    category: 'Content'
  },

  // COMPLETELY REBUILT: GlobalHealth Innovations jobs (22 total across 5 locations: 10+6+3+2+1)
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `health-boston-${i + 1}`,
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    title: [
      'Medical Device Engineer', 'Clinical Data Scientist', 'Regulatory Affairs Specialist', 'Biomedical Engineer', 'Research Scientist',
      'Clinical Trial Manager', 'Medical Software Developer', 'Quality Assurance Specialist', 'Healthcare Data Analyst', 'Medical AI Researcher'
    ]?.[i],
    isFeatured: i === 0,
    descriptionHtml: `<p>Join our ${[
      'Medical Device Engineering', 'Clinical Data Science', 'Regulatory Affairs', 'Biomedical Engineering', 'Research Science',
      'Clinical Trial Management', 'Medical Software Development', 'Quality Assurance', 'Healthcare Data Analysis', 'Medical AI Research'
    ]?.[i]} team at GlobalHealth Innovations.</p>`,
    locations: [{ locationId: 'loc-health-boston', city: 'Boston', region: 'Massachusetts', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: i < 5 ? 'ONSITE' : 'HYBRID',
    salary: { currency: '$', min: 100000 + (i * 4000), max: 140000 + (i * 4000), unit: 'YEAR' },
    postedAt: new Date(now.getTime() - (i + 30) * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + (45 - i * 2) * 24 * 60 * 60 * 1000)?.toISOString(),
    status: i === 6 || i === 9 ? 'CLOSED' : 'ACTIVE',
    locationStatuses: { 'loc-health-boston': { status: i === 6 || i === 9 ? 'CLOSED' : 'ACTIVE', applications: 16 + i, validThrough: new Date(now.getTime() + (45 - i * 2) * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: [
      ['Medical Devices', 'FDA'], ['Clinical Data', 'Biostatistics'], ['FDA Regulations', 'Compliance'], ['Biomedical Engineering', 'Product Development'], ['Medical Research', 'Clinical'],
      ['Clinical Trials', 'Project Management'], ['Medical Software', 'C++'], ['Quality Assurance', 'ISO 13485'], ['Healthcare Data', 'SQL'], ['Medical AI', 'Machine Learning']
    ]?.[i],
    experienceLevel: i < 3 ? 'SENIOR' : i < 7 ? 'MID' : 'JUNIOR',
    category: i < 7 ? 'Healthcare' : 'Data'
  })),

  ...Array.from({ length: 6 }, (_, i) => ({
    id: `health-atlanta-${i + 1}`,
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    title: ['Biomedical Researcher', 'Clinical Research Coordinator', 'Medical Device Tester', 'Pharmaceutical Analyst', 'Healthcare Systems Analyst', 'Medical Technical Writer']?.[i],
    descriptionHtml: `<p>Join our ${['Biomedical Research', 'Clinical Research Coordination', 'Medical Device Testing', 'Pharmaceutical Analysis', 'Healthcare Systems Analysis', 'Medical Technical Writing']?.[i]} team in Atlanta.</p>`,
    locations: [{ locationId: 'loc-health-atlanta', city: 'Atlanta', region: 'Georgia', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: i < 3 ? 'ONSITE' : 'HYBRID',
    salary: { currency: '$', min: 85000 + (i * 4000), max: 120000 + (i * 4000), unit: 'YEAR' },
    postedAt: new Date(now.getTime() - (i + 40) * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + (40 - i * 2) * 24 * 60 * 60 * 1000)?.toISOString(),
    status: i === 3 ? 'CLOSED' : 'ACTIVE',
    locationStatuses: { 'loc-health-atlanta': { status: i === 3 ? 'CLOSED' : 'ACTIVE', applications: 12 + i, validThrough: new Date(now.getTime() + (40 - i * 2) * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: [['Research', 'Biology'], ['Clinical', 'Coordination'], ['Testing', 'Medical Devices'], ['Pharma', 'Analysis'], ['Healthcare', 'Systems'], ['Writing', 'Medical']]?.[i],
    experienceLevel: i < 2 ? 'SENIOR' : 'MID',
    category: 'Healthcare'
  })),

  ...Array.from({ length: 3 }, (_, i) => ({
    id: `health-phoenix-${i + 1}`,
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    title: ['Telemedicine Specialist', 'Health Informatics Analyst', 'Medical Product Manager']?.[i],
    descriptionHtml: `<p>Join our ${['Telemedicine', 'Health Informatics', 'Medical Product Management']?.[i]} team in Phoenix.</p>`,
    locations: [{ locationId: 'loc-health-phoenix', city: 'Phoenix', region: 'Arizona', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: 'HYBRID',
    salary: { currency: '$', min: 95000 + (i * 5000), max: 125000 + (i * 5000), unit: 'YEAR' },
    postedAt: new Date(now.getTime() - (i + 46) * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + (35 - i * 3) * 24 * 60 * 60 * 1000)?.toISOString(),
    status: 'ACTIVE',
    locationStatuses: { 'loc-health-phoenix': { status: 'ACTIVE', applications: 10 + i, validThrough: new Date(now.getTime() + (35 - i * 3) * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: [['Telemedicine', 'Digital Health'], ['Health Informatics', 'Data'], ['Product Management', 'Healthcare']]?.[i],
    experienceLevel: i < 2 ? 'SENIOR' : 'MID',
    category: 'Healthcare'
  })),

  ...Array.from({ length: 2 }, (_, i) => ({
    id: `health-denver-${i + 1}`,
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    title: ['Medical Compliance Officer', 'Clinical Operations Manager']?.[i],
    descriptionHtml: `<p>Join our ${['Medical Compliance', 'Clinical Operations Management']?.[i]} team in Denver.</p>`,
    locations: [{ locationId: 'loc-health-denver', city: 'Denver', region: 'Colorado', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: 'HYBRID',
    salary: { currency: '$', min: 105000 + (i * 10000), max: 135000 + (i * 10000), unit: 'YEAR' },
    postedAt: new Date(now.getTime() - (i + 49) * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + (45 - i * 5) * 24 * 60 * 60 * 1000)?.toISOString(),
    status: 'ACTIVE',
    locationStatuses: { 'loc-health-denver': { status: 'ACTIVE', applications: 8 + i, validThrough: new Date(now.getTime() + (45 - i * 5) * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: [['Compliance', 'Medical'], ['Operations', 'Management']]?.[i],
    experienceLevel: 'SENIOR',
    category: 'Healthcare'
  })),

  {
    id: 'health-minneapolis-1',
    companyId: 'company-2',
    companySlug: 'globalealth-innovations',
    companyName: 'GlobalHealth Innovations',
    title: 'Senior Medical Consultant',
    descriptionHtml: '<p>Join our Senior Medical Consulting team in Minneapolis.</p>',
    locations: [{ locationId: 'loc-health-minneapolis', city: 'Minneapolis', region: 'Minnesota', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: 'HYBRID',
    salary: { currency: '$', min: 150000, max: 180000, unit: 'YEAR' },
    postedAt: new Date(now.getTime() - 51 * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000)?.toISOString(),
    status: 'ACTIVE',
    locationStatuses: { 'loc-health-minneapolis': { status: 'ACTIVE', applications: 25, validThrough: new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: ['Medical Consulting', 'Senior Level', 'Strategic'],
    experienceLevel: 'SENIOR',
    category: 'Healthcare'
  },

  // NEW: Tech Innovations Global jobs (2 total: 1 SF + 1 Remote)
  {
    id: 'job-senior-fullstack-sf',
    companyId: 'company-6',
    companySlug: 'tech-innovations-global',
    companyName: 'Tech Innovations Global',
    title: 'Senior Full-Stack Engineer',
    isFeatured: false,
    descriptionHtml: '<p>Join Tech Innovations Global as a Senior Full-Stack Engineer at our San Francisco Innovation Center. Build next-generation AI-powered applications.</p>',
    locations: [{ locationId: 'loc-job-senior-fullstack-sf-0', city: 'San Francisco', region: 'California', country: 'USA' }],
    employmentType: 'FULL_TIME',
    workMode: 'HYBRID',
    salary: { currency: '$', min: 140000, max: 180000, unit: 'YEAR' },
    postedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000)?.toISOString(),
    status: 'ACTIVE',
    locationStatuses: { 'loc-job-senior-fullstack-sf-0': { status: 'ACTIVE', applications: 12, validThrough: new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: ['React', 'Node.js', 'TypeScript', 'PostgreSQL', 'AWS'],
    experienceLevel: 'SENIOR',
    category: 'Engineering'
  },
  {
    id: 'job-product-manager-remote',
    companyId: 'company-6',
    companySlug: 'tech-innovations-global',
    companyName: 'Tech Innovations Global',
    title: 'Senior Product Manager - AI Platform',
    isFeatured: false,
    descriptionHtml: '<p>Lead product strategy for Tech Innovations Global flagship AI platform from anywhere in the world. This fully remote position offers global impact.</p>',
    locations: [{ locationId: 'loc-product-manager-remote-0', city: 'Remote', region: 'Global', country: 'Global' }],
    employmentType: 'FULL_TIME',
    workMode: 'REMOTE',
    salary: { currency: '$', min: 120000, max: 160000, unit: 'YEAR' },
    postedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000)?.toISOString(),
    validThrough: new Date(now.getTime() + 45 * 24 * 60 * 60 * 1000)?.toISOString(),
    status: 'ACTIVE',
    locationStatuses: { 'loc-product-manager-remote-0': { status: 'ACTIVE', applications: 8, validThrough: new Date(now.getTime() + 45 * 24 * 60 * 60 * 1000)?.toISOString() }},
    tags: ['Product Strategy', 'User Research', 'Data Analysis', 'Agile', 'AI/ML'],
    experienceLevel: 'SENIOR',
    category: 'Product'
  }
];

// Featured jobs array (subset of main jobs marked as featured)
export const featuredJobs = jobs?.filter(job => job?.isFeatured) || [];

// Enhanced searchData with complete location information and consistent patterns
export const searchData = [
  // Jobs with location status information
  ...jobs?.map(job => ({
    ...job,
    keywords: [...(job?.tags || []), job?.companyName?.toLowerCase(), job?.title?.toLowerCase()?.split(' ')]?.flat()
  })),
  
  // Company entries with location information
  ...companies?.map((company, index) => ({
    id: `company_${index + 1}`,
    type: 'company',
    name: company?.name,
    slug: company?.slug,
    url: `/c/${company?.slug}`,
    summary: company?.summary,
    logoUrl: company?.logoUrl,
    industry: company?.industry,
    sizeRange: company?.sizeRange,
    hqCity: company?.hqCity,
    hqRegion: company?.hqRegion,
    openRoles: company?.openRoles,
    locations: company?.locations,
    rating: Math.round((Math.random() * 2 + 3.5) * 10) / 10,
    description: `${company?.name} operates across multiple locations with diverse opportunities.`,
    keywords: [
      company?.name?.toLowerCase(),
      company?.industry?.toLowerCase(),
      ...company?.locations?.map(loc => loc?.toLowerCase()),
      'multi-location',
      'global',
      'international'
    ]?.filter(Boolean)
  })),
  
  // Location entries with status information
  ...locations?.map(location => ({
    ...location,
    type: 'location',
    keywords: [location?.city?.toLowerCase(), location?.region?.toLowerCase(), location?.companyName?.toLowerCase()]
  }))
];

// Helper functions for data manipulation with proper error handling
export const getJobLocationStatus = (jobId, locationId) => {
  const job = jobs?.find(j => j?.id === jobId);
  return job?.locationStatuses?.[locationId] || { status: 'UNKNOWN', applications: 0 };
};

export const getActiveLocationsForJob = (jobId) => {
  const job = jobs?.find(j => j?.id === jobId);
  if (!job?.locationStatuses) return job?.locations || [];
  
  return job?.locations?.filter(location => 
    job?.locationStatuses?.[location?.locationId]?.status === 'ACTIVE'
  ) || [];
};

export const getCompanyJobsWithLocationStatus = (companySlug) => {
  return jobs?.filter(job => job?.companySlug === companySlug)?.map(job => ({
    ...job,
    activeLocations: getActiveLocationsForJob(job?.id)?.length,
    totalLocations: job?.locations?.length || 0
  }));
};

export const getJobsByLocation = (locationId) => {
  return jobs?.filter(job => 
    job?.locations?.some(loc => loc?.locationId === locationId)
  );
};

export const getLocationsByCompany = (companySlug) => {
  return locations?.filter(location => location?.companySlug === companySlug);
};

// FIXED: Consistent exports with proper structure
export { jobs, companies, locations };
export default { jobs, companies, locations, featuredJobs };